import { useMemo } from 'react';

import _ from 'lodash';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  buildWorksheetComputedHint,
  computeWorksheetFieldValue,
  getWorksheetComputedFields,
} from './utils';

/**
 * Keeps a worksheet's computed fields (`compute: payment(rate, term, amount)` in the field's
 * description — LEN-194: the Chattel Revenue Sheet's customer and investor payments) up to date as
 * the answers and calculated values they read change. Worksheet calculation fields cannot raise to
 * a power, so the amortized payment is worked out here and saved as an answer that calculation
 * fields (Total of Payments, the split) then read. A field waits, unchanged, while any argument is
 * unanswered.
 *
 * @param {{ fields: Array, form: Object, calculatedValues: Array<{ name: string, value: string|null }>, setInForm: Function }} params
 * @returns {Object<string, string>} the hint to show under each computed field, by field id
 */
export const useWorksheetComputedFields = ({ fields, form, calculatedValues, setInForm }) => {
  const computedFields = useMemo(() => getWorksheetComputedFields(fields), [fields]);
  const fieldsByName = useMemo(() => _.keyBy(fields, 'name'), [fields]);
  const calculatedValuesByName = useMemo(
    () => _.keyBy(calculatedValues, 'name'),
    [calculatedValues],
  );

  useEffectSafeStrictMode(() => {
    computedFields.forEach(({ id, compute }) => {
      const value = computeWorksheetFieldValue({
        compute,
        fieldsByName,
        form,
        calculatedValuesByName,
      });

      if (!_.isNull(value) && String(form?.[id] ?? '') !== value) {
        setInForm(id, value, false);
      }
    });
  }, [computedFields, fieldsByName, form, calculatedValuesByName, setInForm]);

  return useMemo(
    () =>
      _.fromPairs(
        computedFields.map(({ id, compute }) => [id, buildWorksheetComputedHint(compute)]),
      ),
    [computedFields],
  );
};
